// prettier-ignore
import { type ConnectProofPayload, getPlatform } from "./helpers";
// prettier-ignore
import { CONNECT_EVENT_ERROR_CODES, SEND_TRANSACTION_ERROR_CODES } from "./types";

import type {
  ConnectEvent,
  ConnectItemReply,
  DeviceInfo,
  DisconnectEvent,
  SendTransactionRpcResponseError,
  SendTransactionRpcResponseSuccess,
  TonProofItemReplySuccess,
} from "./types";

export const getDeviceInfo = (appVersion: string, maxMessages: number): DeviceInfo => ({
  platform: getPlatform(),
  appName: "Architec.ton",
  appVersion,
  maxProtocolVersion: 2,
  features: [
    "SendTransaction",
    {
      name: "SendTransaction",
      maxMessages,
    },
  ],
});

export const connectWalletResponse = (options: {
  maxMessages: number;
  replyItems: ConnectItemReply[];
  appVersion: string;
}): ConnectEvent => ({
  id: Date.now(),
  event: "connect",
  payload: {
    items: options.replyItems,
    device: getDeviceInfo(options.appVersion, options.maxMessages),
  },
});

export const connectRejectResponse = (): ConnectEvent => ({
  id: Date.now(),
  event: "connect_error",
  payload: {
    code: CONNECT_EVENT_ERROR_CODES.USER_REJECTS_ERROR,
    message: "User declined the connection",
  },
});

export const disconnectResponse = (id: number | string): DisconnectEvent => ({
  event: "disconnect",
  id,
  payload: {},
});

export const sendTransactionErrorResponse = (id: number | string): SendTransactionRpcResponseError => ({
  id,
  error: {
    code: SEND_TRANSACTION_ERROR_CODES.USER_REJECTS_ERROR,
    message: "Reject Request",
  },
});

export const sendTransactionSuccessResponse = (
  id: number | string,
  boc: string,
): SendTransactionRpcResponseSuccess => ({
  id,
  result: boc,
});

export const sendBadRequestResponse = (id: number | string, message?: string): SendTransactionRpcResponseError => ({
  error: {
    code: SEND_TRANSACTION_ERROR_CODES.BAD_REQUEST_ERROR,
    message: message ?? "Bad Request",
  },
  id,
});

export const createTonProofItem = (signature: Uint8Array, proof: ConnectProofPayload) => ({
  timestamp: proof.timestamp, // 64-bit unix epoch time of the signing operation (seconds)
  domain: {
    lengthBytes: proof.domainBuffer.byteLength, // AppDomain Length
    value: proof.domainBuffer.toString("utf8"), // app domain name (as url part, without encoding)
  },
  signature: Buffer.from(signature).toString("base64"), // base64-encoded signature
  payload: proof.payload, // payload from the request,
});

export const toTonProofItemReply = async (options: {
  signTonConnect: (bufferToSign: Buffer) => Promise<Uint8Array>;
  proof: ConnectProofPayload;
}): Promise<TonProofItemReplySuccess> => {
  const signature = await options.signTonConnect(options.proof.bufferToSign);

  const tonProofItem = createTonProofItem(signature, options.proof);

  return {
    name: "ton_proof",
    proof: tonProofItem,
  };
};
